/**
 * "Tras crear la cuenta o pagar, continúa la acción original" (docs/13 §5.1). The action was saved
 * in `store.ts` before the dialog opened or the tab left for Stripe; this is what picks it up.
 */
import { AkpError } from '@akpl/shared';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { useAuth } from '../auth';
import { useSing, useToggleFavorite, useViewer, useWebT } from './hooks';
import { useWebSession } from './session/WebSessionProvider';
import { type Intent, takeIntent, useWebUi } from './store';

let requestPaymentResume: (() => void) | null = null;

/**
 * Continues the saved action once `me.plan` says Premium. It waits for that instead of running at
 * once because `useSing` reads the plan: with the old `/me` still rendered, a song just paid for
 * would open the paywall again. The webhook can lag (§5.8), so the action stays saved until then.
 */
export function resumeAfterPayment(): void {
  requestPaymentResume?.();
}

/** An intent lives in `sessionStorage`, but it never gets to navigate out of the app. */
export function isInternalPath(to: string): boolean {
  return to.startsWith('/') && !to.startsWith('//');
}

/** Mounted once, by `AccountLifecycle` in `WebLayout`. */
export function useResumeIntent(): void {
  const t = useWebT();
  const auth = useAuth();
  const viewer = useViewer();
  const navigate = useNavigate();
  const sing = useSing();
  const toggleFavorite = useToggleFavorite();
  const session = useWebSession();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const showToast = useWebUi((s) => s.showToast);
  const setPaywallAfterAuth = useWebUi((s) => s.setPaywallAfterAuth);

  const run = useCallback(
    async (intent: Intent) => {
      try {
        switch (intent.type) {
          case 'sing': {
            const { song } = await api.webSong({ params: { idOrSlug: intent.songId } });
            // `useSing` decides from the plan that is now known: an account that turns out to be
            // Premium sings right away, a free one gets W-05 with the song and the intent saved
            // again for after the payment (§5.3). That covers `paywallAfterAuth`, set when all we
            // knew was that a visitor had picked a Premium song.
            await sing(song, intent.tone);
            break;
          }
          case 'openSong': {
            const { song } = await api.webSong({ params: { idOrSlug: intent.songId } });
            openOverlay({ kind: 'song', song });
            break;
          }
          case 'favorite':
            // A `PUT` is idempotent, so a song that was already a favorite stays one.
            toggleFavorite(intent.songId, false);
            break;
          case 'party':
            await session.setMode('party');
            break;
          case 'path':
            if (isInternalPath(intent.to)) await navigate({ href: intent.to });
            break;
        }
      } catch (error) {
        const limited = error instanceof AkpError && error.code === 'rate_limited';
        showToast({
          message: t(limited ? 'web:common.errors.rateLimited' : 'web:common.errors.generic'),
          tone: 'error',
        });
      }
    },
    [navigate, openOverlay, session, showToast, sing, t, toggleFavorite],
  );

  // Only the step from visitor to account counts. An account that reloads the page may have an
  // intent waiting for its payment, and that one belongs to `resumeAfterPayment`.
  const wasAccount = useRef(auth.isAccount);
  const pending = useRef(false);
  useEffect(() => {
    if (auth.isAccount && !wasAccount.current) pending.current = true;
    wasAccount.current = auth.isAccount;
    // `viewer.loading`: until `/me` answers, a Premium account that just logged in looks free.
    if (!pending.current || viewer.loading) return;
    pending.current = false;
    setPaywallAfterAuth(false);
    const intent = takeIntent();
    if (intent) void run(intent);
  }, [auth.isAccount, run, setPaywallAfterAuth, viewer.loading]);

  const paymentPending = useRef(false);
  const [paymentRequest, setPaymentRequest] = useState(0);
  useEffect(() => {
    requestPaymentResume = () => {
      paymentPending.current = true;
      setPaymentRequest((n) => n + 1);
    };
    return () => {
      requestPaymentResume = null;
    };
  }, []);
  useEffect(() => {
    if (!paymentPending.current || viewer.tier !== 'premium') return;
    paymentPending.current = false;
    const intent = takeIntent();
    if (intent) void run(intent);
  }, [paymentRequest, run, viewer.tier]);
}
